"use strict";
// Target Resolution: the named output sizes a user picks on an upscale tool's
// SINGLE_SELECT field, and the rule that stops a Non-Upscaling Target before a
// run is charged for.
//
// See docs/adr/0033-non-upscaling-targets-rejected-before-token-debit.md and
// CONTEXT.md "Tool inputs & field logic".
//
// Written without `?.` / `??`: web's webpack-4 parser reads the symlinked shared
// dist without transpiling it, and cannot parse either operator. Same constraint
// as runnitFields/types, kits/resolveKit, and analytics/identity.
//
// This module reads the Firestore field spelling (`__rgroup` / `__rfield` /
// `fieldDefUuid`), not the published API-v2 spelling.
Object.defineProperty(exports, "__esModule", { value: true });
exports.targetResolutionOptionValues = exports.evaluateTargetLargerThanInput = exports.smallestUpscalingOption = exports.isNonUpscalingTarget = exports.extractInputMediaDimensions = exports.resolveTargetResolution = exports.PROVIDER_CANONICAL_TARGET_RESOLUTIONS = exports.TARGET_RESOLUTION_DIMENSIONS = void 0;
const options_1 = require("../runnitFields/options");
// Deliberately a closed set. Tools needing arbitrary output sizes use
// UPSCALE_FACTOR_SLIDER, which already owns that case.
//
// `4k` is an alias for `2160p`, and the vocabulary is intentionally wider than
// any single provider accepts: SeedVR's worker
// (queueGpuJobs/.../SeedVRUpscaleVideoEndpointProcessor.ts) takes only
// 720p | 1080p | 1440p | 2160p, so a tool authored with `4k` passes this size
// guard and still fails there. This rule is about SIZE, not about whether a
// provider recognises a name. Provider-value validation is a separate concern.
exports.TARGET_RESOLUTION_DIMENSIONS = Object.freeze({
    '720p': { width: 1280, height: 720 },
    '1080p': { width: 1920, height: 1080 },
    '1440p': { width: 2560, height: 1440 },
    '2160p': { width: 3840, height: 2160 },
    '4k': { width: 3840, height: 2160 },
});
// The four names SeedVR's worker accepts verbatim. Everything in this set must
// resolve to the same dimensions as the worker's own `limits` table; `4k` is
// deliberately outside it. Exported so a test can pin the parity rather than
// leaving it to prose.
exports.PROVIDER_CANONICAL_TARGET_RESOLUTIONS = Object.freeze([
    '720p', '1080p', '1440p', '2160p',
]);
// SINGLE_SELECT values arrive either raw ('1080p') or wrapped ({ value: '1080p' }).
// Same unwrap idiom as validateSingleSelect in ../runnitFields/validate.ts.
const unwrapSelectValue = (value) => {
    if (value && typeof value === 'object' && 'value' in value)
        return value.value;
    return value;
};
const dimensionsOf = (holder) => {
    if (!holder)
        return null;
    const width = holder.width;
    const height = holder.height;
    if (!width || !height)
        return null;
    return { width, height };
};
/**
 * Dimensions for a named Target Resolution, or null for anything unrecognised.
 *
 * Matching is case-insensitive, which deliberately diverges from
 * ../runnitFields/validate.ts (it compares option values exactly). Normalizing
 * here means a sloppily authored '1080P' still gets guarded rather than
 * silently skipping the rule. It does not make such a value valid anywhere else.
 *
 * A null result disables the rule rather than blocking, so an unknown value
 * behaves exactly as it did before this rule existed.
 */
const resolveTargetResolution = (value) => {
    const raw = unwrapSelectValue(value);
    if (typeof raw !== 'string' && typeof raw !== 'number')
        return null;
    const key = String(raw).trim().toLowerCase();
    // hasOwnProperty, not a bare lookup: 'constructor' / 'toString' would otherwise
    // resolve to an inherited Object.prototype member, which is truthy, has no
    // width/height, and poisons smallestUpscalingOption's minimum with NaN.
    if (!Object.prototype.hasOwnProperty.call(exports.TARGET_RESOLUTION_DIMENSIONS, key))
        return null;
    return exports.TARGET_RESOLUTION_DIMENSIONS[key] || null;
};
exports.resolveTargetResolution = resolveTargetResolution;
/**
 * Width/height out of a Runnit input value, across the image upload, image
 * nodeRunResult, video upload, and video nodeRunResult shapes.
 *
 * The single reader for this shape: web's `upscaleFactorError` calls it too.
 * Video deliberately takes precedence over image, matching the ladder that was
 * already in RunRunnitButton. Note `web/src/utils/aspectRatio.ts` has a similar
 * ladder that coalesces with `||`, making video a LAST resort instead of an
 * override, so it is not interchangeable with this one.
 */
const extractInputMediaDimensions = (inputValue) => {
    if (!inputValue || typeof inputValue !== 'object')
        return null;
    const image = inputValue.image || {};
    const video = inputValue.video || {};
    // Video first, because it overrides image when both are present.
    if (video.nodeRunResult)
        return dimensionsOf(video.nodeRunResult.file);
    if (video.upload)
        return dimensionsOf(video.upload);
    if (image.nodeRunResult)
        return dimensionsOf(image.nodeRunResult.file);
    if (inputValue.nodeRunResult)
        return dimensionsOf(inputValue.nodeRunResult.file);
    if (image.upload)
        return dimensionsOf(image.upload);
    if (inputValue.upload)
        return dimensionsOf(inputValue.upload);
    return null;
};
exports.extractInputMediaDimensions = extractInputMediaDimensions;
/**
 * True when the target would not enlarge the input on at least one dimension.
 *
 * This is a derivation, not a stricter house rule. An upscale factor is
 * min(tw/iw, th/ih), so `factor <= 1` is algebraically identical to
 * `iw >= tw || ih >= th`. The per-dimension `>=` therefore covers BOTH halves
 * of a Non-Upscaling Target: the case a worker rejects outright (a dimension
 * exceeds its target) and the case it silently permits (a dimension equals its
 * target, factor exactly 1.0, run completes and bills). 1920x800 against 1080p
 * is the second; 1920x1088 against 1080p is the first.
 */
const isNonUpscalingTarget = (input, target) => (input.width >= target.width || input.height >= target.height);
exports.isNonUpscalingTarget = isNonUpscalingTarget;
/**
 * The smallest of `optionValues` that would actually enlarge the input, or null
 * when the field offers nothing large enough.
 *
 * Derived from the field's OWN options rather than the global map, so a tool is
 * never told to pick something it does not offer.
 */
const smallestUpscalingOption = (input, optionValues) => {
    let bestLabel = null;
    let bestArea = Infinity;
    for (const optionValue of (optionValues || [])) {
        const target = (0, exports.resolveTargetResolution)(optionValue);
        if (!target)
            continue;
        if ((0, exports.isNonUpscalingTarget)(input, target))
            continue;
        const area = target.width * target.height;
        if (area >= bestArea)
            continue;
        bestArea = area;
        bestLabel = String(unwrapSelectValue(optionValue));
    }
    return bestLabel;
};
exports.smallestUpscalingOption = smallestUpscalingOption;
/**
 * The blocked reason for a Non-Upscaling Target, or null to allow.
 *
 * No-ops (returns null, never throws) when the input dimensions are unknown or
 * the selected value has no known Target Resolution. That is what makes this
 * fail open: unresolvable media preserves the behaviour that existed before.
 */
const evaluateTargetLargerThanInput = (args) => {
    const { inputDimensions, selectedValue, optionValues, fieldLabel, message } = args;
    if (!inputDimensions || !inputDimensions.width || !inputDimensions.height)
        return null;
    const target = (0, exports.resolveTargetResolution)(selectedValue);
    if (!target)
        return null;
    if (!(0, exports.isNonUpscalingTarget)(inputDimensions, target))
        return null;
    const targetLabel = String(unwrapSelectValue(selectedValue));
    const inputSize = `${inputDimensions.width}x${inputDimensions.height}`;
    const prefix = fieldLabel ? `${fieldLabel}: ` : '';
    // The label is prefixed to an override too. RunRunnitButton joins every reason
    // into one "Form invalid, check fields: ..." string, so a bare custom message
    // would not say which control to change.
    if (message)
        return `${prefix}${message}`;
    const suggestion = (0, exports.smallestUpscalingOption)(inputDimensions, optionValues || []);
    const advice = suggestion
        ? `Choose ${suggestion} or higher.`
        : `No available option is larger than your ${inputSize} input.`;
    return `${prefix}${targetLabel} targets ${target.width}x${target.height}, which is not larger than your ${inputSize} input. ${advice}`;
};
exports.evaluateTargetLargerThanInput = evaluateTargetLargerThanInput;
/**
 * Convenience wrapper for callers holding a SINGLE_SELECT field definition:
 * pulls the option values off whichever display key the field uses.
 */
const targetResolutionOptionValues = (display) => ((0, options_1.getSingleSelectOptions)(display).map((option) => (option ? option.value : undefined)));
exports.targetResolutionOptionValues = targetResolutionOptionValues;
